<template>
  <div
    :class="[
      $style.root,
      `design-system-input flex w-full items-center rounded-lg border transition`,
      `outline-2 -outline-offset-1`,
      $slots.before ? `pl-2.5` : `pl-0`,
      $slots.after ? `pr-2.5` : `pr-0`,
      isDisabled && `opacity-50`,
      !isDisabled && readonly && `bg-sidebar`,
      fieldContext?.validationStatus.value === 'error'
        ? `border-red-300 outline-red-500 dark:border-red-800`
        : `outline-blue-500`,
    ]"
  >
    <slot name="before" />

    <input
      :id="fieldContext?.inputId"
      ref="input"
      v-model="modelValue"
      :class="[
        $style.input,
        `block w-full border-none bg-transparent px-2.5 py-1.5 text-sm text-current placeholder:text-muted-foreground read-only:bg-transparent read-only:text-current focus:outline-none focus:ring-0 disabled:bg-transparent disabled:text-current`,
      ]"
      :type="type"
      :placeholder="placeholder"
      :readonly="readonly"
      :required="fieldContext?.required.value"
      :disabled="isDisabled"
      :name="props.name"
      :autocomplete="autocomplete"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :aria-describedby="ariaDescribedBy"
      v-bind="$attrs"
      @keydown="onKeydown"
    />

    <slot name="after" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, useTemplateRef } from "vue";

import { injectFieldContext } from "./context";

defineOptions({
  inheritAttrs: false,
});

const modelValue = defineModel<string>({ default: "" });

const props = withDefaults(
  defineProps<{
    type?: "date" | "email" | "number" | "password" | "text" | "url";
    placeholder?: string;
    readonly?: boolean;
    disabled?: boolean;
    name?: string;
    autocomplete?: string;
    autofocus?: boolean;
  }>(),
  {
    type: "text",
  }
);

const input = useTemplateRef("input");

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

const onKeydown = (ev: KeyboardEvent) => {
  // Don't allow Enter to be used for form submissions, forms should lean to
  // using $mod+Enter instead for allowing quick submissions.
  if (
    ev.key === "Enter" &&
    !(ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey)
  ) {
    ev.preventDefault();
  }
};

onMounted(() => {
  if (props.autofocus) {
    input.value?.focus({ preventScroll: true });
  }
});

defineExpose({ input });
</script>

<style module>
.root:has(.input:focus-visible) {
  outline-style: solid;
}
</style>
